import React, { useState } from 'react';
import styled from '@emotion/styled';
import { sliderService } from '../admin/services/sliderService';
import { boardService } from '../admin/services/boardService';
import { authService } from '../admin/services/authService';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #f9f9f9;
`;

const Title = styled.h2`
  margin-bottom: 1rem;
  color: #333;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #0056b3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ResultBox = styled.pre`
  margin-top: 1rem;
  padding: 1rem;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 400px;
  overflow-y: auto;
`;

const ErrorBox = styled(ResultBox)`
  background: #fee;
  border-color: #fcc;
  color: #c00;
`;

const Input = styled.input`
  padding: 0.5rem;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 200px;
`;

export const ApiTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // 로그인 상태
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('admin1234');

  const handleApiCall = async (apiFunction: () => Promise<any>, description: string) => {
    setLoading(true);
    setResult(null);
    setError(null);

    try {
      console.log(`Testing: ${description}`);
      const data = await apiFunction();
      console.log('Success:', data);
      setResult(data);
    } catch (err: any) {
      console.error('Error:', err);
      setError(err.message || err.toString());
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <h1>API 연동 테스트</h1>
      <p>백엔드 API: http://localhost:8081/api</p>

      {/* 인증 테스트 */}
      <Section>
        <Title>1. 인증 API 테스트</Title>
        <div>
          <Input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
          />
          <Button
            onClick={() => handleApiCall(
              () => authService.login({ username, password }),
              'POST /admin/login'
            )}
            disabled={loading}
          >
            로그인 테스트
          </Button>
        </div>
      </Section>

      {/* 슬라이더 테스트 */}
      <Section>
        <Title>2. 슬라이더 API 테스트</Title>
        <Button
          onClick={() => handleApiCall(
            () => sliderService.getSliders(),
            'GET /slider'
          )}
          disabled={loading}
        >
          슬라이더 목록 조회
        </Button>
      </Section>

      {/* 게시판 테스트 */}
      <Section>
        <Title>3. 게시판 API 테스트</Title>
        <Button
          onClick={() => handleApiCall(
            () => boardService.getPosts('sermon-sunday-morning', 1, 10),
            'GET /board/sunday-morning'
          )}
          disabled={loading}
        >
          주일 오전예배 목록
        </Button>
        <Button
          onClick={() => handleApiCall(
            () => boardService.getPosts('news', 1, 10),
            'GET /board/news'
          )}
          disabled={loading}
        >
          교회소식 목록
        </Button>
        <Button
          onClick={() => handleApiCall(
            () => boardService.getPosts('gallery', 1, 10),
            'GET /board/gallery'
          )}
          disabled={loading}
        >
          갤러리 목록
        </Button>
      </Section>

      {/* 결과 표시 */}
      {loading && <p>Loading...</p>}
      {error && (
        <ErrorBox>
          <strong>Error:</strong>
          <br />
          {error}
        </ErrorBox>
      )}
      {result && (
        <ResultBox>
          <strong>Result:</strong>
          <br />
          {JSON.stringify(result, null, 2)}
        </ResultBox>
      )}
    </Container>
  );
};
